<template>
  <div class="pane-header" :class="{ 'is-ga': domain.isGa }">
    <div class="basic-info">
      <div class="domain-state">
        <template v-if="!domain.isGa">
          <div class="main-button" :class="{ off: mainDomain !== domain.orgSid }">
            <button class="to-main" @click="toggleMainReport">
              <i v-if="mainDomain === domain.orgSid" class="icon dashboard--star size-24"></i>
              <i v-else class="icon dashboard--star size-20 off"></i>
            </button>
          </div>
          <div class="shared-icon" v-if="(isActivated || !isAnalyzing) && domain.shared">
            <i class="icon" :class="`icon--report-${domain.shared}`" v-btf-tooltip:bottom.start="$t(`share.sharedType.${domain.shared}.tooltip`, {total: sharedCount})" />
          </div>
        </template>
      </div>
      <div class="name" ref="domainTitle">{{ domain.domainName }}</div>
      <div class="main-info">
        <div :class="['match-type', domain.matchType]">
          {{ domain.matchType }}
        </div>
        <div class="domain url" :class="{ off: !isActivated }">
          {{ domain.domain | domain }}
        </div>
        <div class="button-wrap" :id="`editDropdown${domain.sid}`">
          <button class="button" @click="showEdit = true">
            <i class="icon icon--setting"></i>
          </button>
          <transition name="slide-fade">
            <div class="edit-dropdown" @mouseenter="resetEditDropdownTimer" @mouseleave="startEditDropdownTimer"
                 v-if="showEdit">
              <button class="edit-option" @click="goEdit" :class="{ disabled: isDemo }">
                {{ $t('common.edit') }}
              </button>
              <button v-if="userData.userId != 'samsung_gmc'" class="edit-option" @click="$emit('set-share')" :class="{ disabled: isDemo }">
                {{ $t('common.share') }}
              </button>
              <button v-if="domain.matchType === 'simple'" class="edit-option" :class="{ disabled: domain.twinSid || isDemo }" @click="$emit('set-exact')">
                {{ $t('common.expand') }}
              </button>
              <button class="edit-option" @click="goDelete" :class="{ disabled: isDemo }">
                {{ $t('common.delete') }}
              </button>
            </div>
          </transition>
        </div>
        <div class="state-wrap">
          <p class="state-wrap-label">
            {{ $t('dashboard.state.label') }}
            <span v-if="!domain.installDate || isAnalyzing || domain.shared === 'shared' || isTempStopped">:</span>
          </p>
          <p class="value uninstalled" v-if="!domain.installDate">{{ $t('dashboard.state.uninstalled') }}</p>
          <p class="value analyzing" v-else-if="isAnalyzing">{{ $t('dashboard.state.analyzing') }}</p>
          <p class="value" :class="{ running: !isNotRunning }" v-else-if="domain.shared === 'shared' || isTempStopped">
            {{ isNotRunning ? $t(`dashboard.state.${domain.uiState}`) : $t('dashboard.state.running') }}
          </p>
          <div class="dropdown" ref="dropdown" v-else @scroll.stop :class="{ disabled: isDemo }">
            <div class="selected" @click="stateDropdownOpen = true">
              <span class="label" :class="{ running: !isNotRunning }">
                {{ isNotRunning ? $t(`dashboard.state.${domain.state}`) : $t('dashboard.state.running') }}
              </span>
              <i class="icon icon--arrow-dropdown" v-if="!isDemo"></i>
            </div>
            <div class="item-list" :class="{ active: !isDemo && stateDropdownOpen }">
              <div class="item-list-item" @click="stateDropdownOpen = false">
                <span class="label">{{ isNotRunning ? $t(`dashboard.state.${domain.state}`) : $t('dashboard.state.running') }}</span>
              </div>
              <div class="item-list-item" v-if="isStopAvailable" @click="confirmChangeState">
                <span class="label">{{ $t('dashboard.state.stop') }}</span>
              </div>
              <div class="item-list-item" v-else @click="confirmChangeState">
                <span class="label">{{ $t('dashboard.state.running') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="goto" :class="{ 'disabled': !domain.installDate || isAnalyzing }">
      <div class="buttons">
        <template v-for="item in gotoList">
          <button :key="item.name" v-if="item.show" :class="[item.name, { disabled: item.disabled || (domain.isGa && item.name !== 'mine') }]" :disabled="item.disabled || (domain.isGa && item.name !== 'mine')" @click="item.click"
          v-btf-tooltip:top="{contents: !isAnalyzing && item.tooltip ? item.tooltip : null, size: '300px'}">
            {{ $t(`dashboard.button.${item.name}`) }}
            <i :class="['icon', `icon--dashboard-${item.name}`]"></i>
          </button>
        </template>
      </div>
      <div class="pv"
           :class="{ exhauted: domain.domainCreditEmpty, has: ipBlock || isOverTraffic || domain.domainCreditEmpty }"
           v-if="domain.installDate && basicData">
        <div class="timezone" v-if="isActivated" v-btf-tooltip:bottom="$t('common.timezone') + ' : (' + timezone + ') ' + domain.timezoneName">
          UTC {{ timezone.split(':')[0] }}
        </div>
        <template v-if="domain.preSamplingRate < 100 || domain.postSamplingRate < 100">
          <div class="pv-sampling-tag">{{ $t('dashboard.samplingTag', { data: Math.min(domain.preSamplingRate, domain.postSamplingRate)}) }}</div>
        </template>
        <span class="pv-label">{{ $t('dashboard.monthlyCollectionPV') }} :</span>
        <span class="pv-limit"> {{ basicData.totalPv | thousand }} &nbsp;/&nbsp; {{ pvLimitText }}</span>
        <ui-tooltip class="ipblock-tooltip" :class="{ large: isOverTraffic }"
                    v-if="ipBlock || isOverTraffic || domain.domainCreditEmpty" arrowTop arrowCenter>
          <span class="tooltip-row" v-if="domain.domainCreditEmpty">
            <span class="tip-title">
              <i class="icon icon--triangle-alert"></i>
              <span class="tip-title-desc exhauted" v-html="$t('dashboard.pvExhauted')"></span>
            </span>
          </span>
          <!-- <div class="tooltip-seperator" v-if="domain.domainCreditEmpty && (ipBlock || isOverTraffic)"></div> -->
          <span class="tooltip-row" v-if="isOverTraffic">
            <span class="tip-title">
              <i class="icon icon--traffic"></i>
              <span class="tip-title-desc" v-html="$t('dashboard.trafficOver')"></span>
            </span>
            <span class="tip-content">
              {{ basicData.totalPv - domain.pvLimit | thousand }}{{ $t('common.pv') }}
            </span>
          </span>
          <!-- <div class="tooltip-seperator" v-if="ipBlock && isOverTraffic"></div> -->
          <span class="tooltip-row" v-if="ipBlock">
            <span class="tip-title">
              <i class="icon icon--deny"></i> <span>{{ $t('dashboard.ipBlock') }}</span>
            </span>
            <span class="tip-content">
              {{ ipBlock }}{{ $t('common.unit.gae') }}
            </span>
          </span>
        </ui-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import UiTooltip from '@/components/ui/UiTooltip'
import { calculateLogDateTime, getOffset, thousand } from '@/libs/utils'
import { changeState } from '@/api/domain'
import { getMineCount } from '@/api/ai'

import { parse, min } from 'date-fns'

import { EventBus } from '@/libs/event-bus'

import { create } from 'vue-modal-dialogs'
import AppDialog from '@/components/ui/AppDialog'
import { isInIframe } from '@/libs/auth-helper'

const openDialog = create(AppDialog)

const now = new Date()
export default {
  name: 'DomainPaneHeader',
  components: {
    UiTooltip
  },
  props: {
    domain: {
      type: Object,
      default: () => { return {} }
    },
    mainDomain: {
      type: String,
      default: ''
    },
    twinDomain: {
      type: Object,
      required: false
    },
    loaded: Boolean,
    basicData: Object,
    sharedCount: Number,
    isAnalyzing: Boolean
  },
  data () {
    return {
      loading: true,
      showEdit: false,
      editDropdownTimer: null,
      editDropdownTimerTime: null,

      todayData: null,
      weeklyData: {
        thisWeek: null,
        lastWeek: null
      },
      isRedline: true,
      ipBlockCount: null,
      mineCount: 0,
      stateDropdownOpen: false
    }
  },
  computed: {
    ...mapState([
      'userData',
      'isDemo',
      'journeyOptions',
      'journeyPeriod'
    ]),
    gotoList () {
      return [
        {
          name: 'survey',
          show: this.domain.matchType !== 'exact',
          disabled: this.domain.shared === 'shared',
          click: () => this.goSurvey(),
          tooltip: this.$t('dashboard.survey.description')
        },
        {
          name: 'ranking',
          show: true,
          disabled: null,
          click: () => this.goPanePage('ranking')
        },
        {
          name: 'journey',
          show: true,
          disabled: null,
          click: () => this.goPanePage('journey')
        },
        {
          name: 'trend',
          show: true,
          disabled: null,
          click: () => this.goPanePage('trend')
        },
        {
          name: 'sitemap',
          show: this.userData.userId === 'admin',
          disabled: null,
          click: () => this.goPanePage('sitemap')
        },
        {
          name: 'mine',
          show: true,
          disabled: !this.mineCount,
          click: () => this.goPanePage('mine')
        }
      ]
    },
    isOverTraffic () {
      return this.domain.pvLimit !== -1 ? this.domain.pvLimit < this.basicData.totalPv : false
    },
    timezone () {
      return getOffset(this.domain.timezoneName)
    },
    ipBlock () {
      if (this.ipBlockCount && this.ipBlockCount > 0) {
        return this.ipBlockCount
      }
      return null
    },
    isNotRunning () {
      return this.domain && (this.domain.state === 'stop' || this.isTempStopped)
    },
    isTempStopped () {
      const tempStopList = ['tempStop', 'endByPvLimit', 'freeover']
      return tempStopList.includes(this.domain.uiState)
    },
    isStopAvailable () {
      return this.domain && this.domain.state !== 'stop'
    },
    isSwitchDisabled () {
      return this.domain.matchType === 'exact' && !this.isStopAvailable && this.twinDomain && this.twinDomain.state === 'stop'
    },
    isActivated () {
      if (this.domain.installDate && !this.isAnalyzing && (!this.domain.shared || this.domain.shared === 'share')) {
        return true
      } else {
        return false
      }
    },
    pvLimitText () {
      if (this.basicData && this.basicData.pvLimit > 0) {
        return thousand(this.basicData.pvLimit)
      }
      return this.$t('common.noLimit')
    },
    isExhauted () {
      return this.domain.userCreditEmpty || this.domain.domainCreditEmpty
    },
    lastDayDataAvailable () {
      if (!this.isExhauted) {
        return calculateLogDateTime(this.domain)
      }
      let minDate = this.domain.domainCreditEmptyDate || this.domain.userCreditEmptyDate
      if (this.domain.domainCreditEmptyDate && this.domain.userCreditEmptyDate) {
        minDate = min(this.domain.domainCreditEmptyDate, this.domain.userCreditEmptyDate)
      }
      return parse(min(now, parse(minDate)))
    }
  },
  watch: {
    domain (value) {
      if (value) {
        this.$emit('onTitleHeightChange', false)
        this.getMineInfo()
      }
    },
    showEdit (value) {
      let bodyListener = (e) => {
        let outside = true

        function composedPath (el) {
          let paths = []
          while (el) {
            paths.push(el)
            if (el.tagName === 'HTML') {
              paths.push(document)
              paths.push(window)
              return paths
            }
            el = el.parentElement
          }
        }

        const path = e.path || (e.composedPath && e.composedPath()) || composedPath(e.target)
        if (path) {
          for (const el of path) {
            if (el.id && el.id === `editDropdown${this.domain.sid}`) {
              outside = false
            }
          }
        }
        if (outside) {
          this.showEdit = false
        }
      }

      if (value) {
        document.body.addEventListener('click', bodyListener, false)
      } else {
        document.body.removeEventListener('click', bodyListener)
      }
    },
    stateDropdownOpen (val) {
      if (val) {
        document.addEventListener('click', this.handleClickOutside)
      } else {
        document.removeEventListener('click', this.handleClickOutside)
      }
    }
  },
  created () {
    if (this.loaded) {
      this.getMineInfo()
    }
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside (event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.stateDropdownOpen = false
      }
    },
    getTitleHeightCheck () {
      if (this.$refs.domainTitle.clientHeight > 100) {
        this.$emit('onTitleHeightChange', true)
      }
    },
    startEditDropdownTimer: function () {
      this.editDropdownTimer = setInterval(() => {
        if (this.editDropdownTimerTime < 3) {
          this.showEdit = false
        }
        this.editDropdownTimerTime--
      }, 1000)
    },
    resetEditDropdownTimer: function () {
      this.editDropdownTimerTime = 3
      clearInterval(this.editDropdownTimer)
      this.editDropdownTimer = null
    },
    getMineInfo () {
      const requestData = {
        parentSid: this.domain.parentSid,
        matchType: this.domain.matchType,
        lang: this.$i18n.locale
      }

      getMineCount(requestData, true).then(res => {
        this.mineCount = res
        this.getTitleHeightCheck()
      })
    },
    confirmChangeState () {
      let optionContent = null
      this.stateDropdownOpen = false
      if (this.domain.matchType === 'simple') {
        optionContent = {
          text: this.$t(`dialog.report.changeOnOff.${this.isStopAvailable ? 'off' : 'on'}.warning`),
          style: 'margin: -16px 0 42px 0; color: #E60724; line-height: 1.5;'
        }
      }
      openDialog({
        dialogType: `report.changeOnOff.${this.isStopAvailable ? 'off' : 'on'}`,
        optionContent,
        result: (data) => {
          if (data) {
            return this.changeState()
          }
        }
      })
    },
    changeState () {
      changeState({
        sid: this.domain.orgSid,
        matchType: this.domain.matchType,
        stop: this.isStopAvailable
      }, true).then(data => {
        this.$emit('getList', this.domain.orgSid)
        openDialog({
          dialogType: `report.changeSuccess.${this.isStopAvailable ? 'off' : 'on'}`
        })
      }).catch(() => {
        EventBus.$emit('onError')
      })
    },
    toggleMainReport () {
      this.$emit('setMainDomain', this.domain)
    },
    goSurvey () {
      if (this.isAnalyzing) {
        return null
      } else {
        EventBus.$emit('onLoading', true)
        setTimeout(() => {
          this.$router.push({
            name: 'userResearch',
            query: { sid: this.domain.orgSid || this.domain.sid }
          })
        }, 300)
      }
    },
    setOnTop (state) {
      this.$emit('setOnTop', state)
    },
    goPanePage (type) {
      const queryData = {
        sid: this.domain.orgSid || this.domain.sid
      }

      if (this.domain.shared === 'shared') {
        queryData.subSid = this.domain.subDomains[0].orgSid
        queryData.path = null
        // queryData.path = this.domain.subDomains[0].firstPaths[0] || null
      }

      if (this.userData.userId === 'samsung_gmc') {
        queryData.subSid = this.domain.subDomains.find(subDomain => subDomain.domain === 'samsung.com').orgSid
        queryData.path = 'uk'
      }

      let routeData = this.$router.resolve({
        name: type,
        query: queryData
      })

      const newWindow = window.open(routeData.href, isInIframe() ? '_self' : '_blank')

      if (type !== 'mine' && newWindow) {
        newWindow._journeyOptionsFromParent = { ...this.journeyOptions }
        newWindow._journeyPeriodFromParent = { ...this.journeyPeriod }
      }
    },
    goEdit () {
      this.showEdit = false
      EventBus.$emit('onLoading', true)
      setTimeout(() => {
        this.$router.push({
          name: 'edit',
          query: { sid: this.domain.orgSid || this.domain.sid }
        })
      }, 300)
    },
    goDelete () {
      this.showEdit = false
      this.$emit('setDeletingDomain', this.domain)
    }
  }
}
</script>

<style lang="scss" scoped>
  .pane-header {
    display: flex;
    justify-content: space-between;
    padding: 16px 4px 12px;
    border-bottom: 1px solid #E0E0E0;

    .basic-info {
      display: flex;
      flex-direction: column;
      gap: 8px;
      .domain-state {
        display: flex;
        align-items: center;
        min-height: 30px;
        gap: 16px;
        .main-button {
          transition: padding-right 0.15s ease;

          .to-main {
            display: flex;
            border-radius: 15px;
            cursor: default;
            transition: box-shadow 0.15s ease;
          }

          &.off {
            .to-main {
              padding: 5px;
              box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.17), 0 0 1px 0 rgba(0, 0, 0, 0.2);
              cursor: pointer;
            }
          }
        }
        .shared-icon {
        }
      }
      .name {
        max-width: 673px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        line-height: 1.2em;
        max-height: 2.4em;
        overflow-wrap: break-word;
        color: #2f2f2f;
        font-size: 42px;
        font-weight: 600;
        line-height: 120%;
      }
      .main-info {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        height: 32px;
        .match-type {
          padding: 4.5px 8px;
          border-radius: 25px;
          background: #2C7899;
          color: #FFF;
          font-size: 13px;
          font-weight: 500;
          text-transform: capitalize;
          &.exact {
            background: #005BAD;
          }
        }
        .domain {
          max-width: 420px;
          overflow: hidden;
          text-overflow: ellipsis;

          color: #2F2F2F;
          font-size: 18px;
          font-weight: 600;
          line-height: 1.2;
        }
        .button-wrap {
          position: relative;
          display: inline-block;

          .button {
            display: flex;
            border-radius: 15px;
            transition: background-color 0.2s ease;
            &:hover {
              background-color: rgba(0, 0, 0, 0.06);
            }
          }

          .edit-dropdown {
            position: absolute;
            top: 25px;
            left: calc(50% + 3px);
            transform: translateX(-50%);
            z-index: 100;
            border-radius: 6px;
            box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
            background-color: #ffffff;
            width: 70px;
            overflow: hidden;

            button {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              padding: 10px 0;
              color: #444444;
              font-size: 14px;
              background: #ffffff;
              transition: background-color 0.2s ease;

              &:hover {
                background-color: #ebebeb;
              }

              &.disabled {
                background-color: #fff;
                color: #C7C7C7;
                pointer-events: none;
                cursor: default;
              }
            }

            &.slide-fade-enter-active {
              transform-origin: top;
              transition: opacity 0.3s ease, transform 0.3s ease;
            }

            &.slide-fade-leave-active {
              transform-origin: top;
              transition: opacity 0.3s ease, transform 0.3s ease;
            }

            &.slide-fade-enter,
            &.slide-fade-leave-to {
              opacity: 0;
              transform: translateX(-50%) scaleY(0);
            }
          }
        }
        .state-wrap {
          display: flex;
          align-items: center;
          gap: 9px;
          padding-left: 8px;
          &-label {
            color: #777;
            font-size: 12px;
            font-weight: 600;
          }
          .value {
            font-size: 13px;
            font-weight: 600;
            &.uninstalled {
              color: #E60724;
            }
            &.analyzing {
              color: #0074FF;
            }
            &.running {
              color: #57AB00;
            }
          }
          .dropdown {
            min-width: 96px;
            position: relative;
            margin-left: 3px;
            font-size: 13px;

            .selected {
              height: 32px;
              padding: 0 6px 0 10px;
              cursor: pointer;
              line-height: 1;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              border-radius: 5px;
              background-color: #fff;
              box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.20);
              .label {
                font-weight: 600;
                display :inline-block;
                color: #666;
                &.running {
                  color: #57AB00;
                }
              }
            }

            &.disabled {
              pointer-events: none;
              opacity: .7;
              color: #666;
            }

            .item-list {
              border-radius: 6px;
              color: #444;
              background-color: #ffffff;
              box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
              display: none;
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              z-index: 1;
              transition: all 250ms ease;
              transition-delay: 250ms;
              transform: translateY(-10px);
              opacity: 0;
              overflow: hidden;
              cursor: pointer;
              &.active {
                display: inline-block;
                transform: translateY(0);
                opacity: 1;
              }
              .item-list-item {
                padding: 10px 12px;
                &:hover {
                  background-color: #ebebeb;
                }
              }
            }
          }
        }
      }
    }

    .goto {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 12px;
      &.disabled .buttons button,
      .buttons button.disabled {
        pointer-events: none;
        opacity: .3;
      }
      .buttons {
        display: flex;
        gap: 4px;

        button {
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 2px;
          width: 76px;
          height: 76px;
          padding: 18px 4px 4px 4px;
          border-radius: 8px;
          vertical-align: top;
          background-color: #ffffff;
          border: 1px solid #ec0047;
          color: #ec0047;
          font-size: 14px;
          font-weight: 600;
          line-height: 100%;
          box-shadow: 0px 5px 10px -3px rgba(0, 0, 0, 0.18);

          &::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 8px;
            background-color: rgba(0, 0, 0, 0.06);
            opacity: 0;
            transition: opacity 0.15s ease;
          }

          &:hover {
            background-color: #ec0047;
            color: #ffffff;
            box-shadow: 0px 5px 10px -3px rgba(0, 0, 0, 0.25);

            .icon {
              filter: brightness(0) invert(1);
            }

            &::after {
              opacity: 1;
            }
          }

          .icon {
            margin-left: auto;
            flex-shrink: 0;
          }

          &.survey {
            margin-right: 20px;
            border: 1px solid #5C68A8;
            color: #5C68A8;
            &:hover {
              color: #fff;
              background-color: #5C68A8;
            }
            &::before {
              content: "";
              position: absolute;
              top: 50%;
              right: -12px;
              transform: translateY(-50%);
              width: 1px;
              height: 48px;
              background-color: #CCC;
            }
          }
          &.mine {
            border: 1px solid #0085FF;
            color: #0085FF;
            &:hover {
              color: #fff;
              background-color: #0085FF;
            }
          }
        }
      }
      .pv {
        @include flexYcenter;
        gap: 8px;
        position: relative;
        min-height: 24px;
        font-size: 13px;
        border-radius: 4px;
        transition: background-color 0.15s ease;
        cursor: default;
        .timezone {
          border-radius: 11px;
          border: solid 1px #d7d7d7;
          background-color: #fff;
          padding: 5px 8px;
          font-size: 12px;
          font-weight: 500;
          line-height: 1;
          color: #444;
          position: relative;
          cursor: default;
          &:hover {
            & .timezone-tooltip {
              display: block;
            }
          }
          & .timezone-tooltip {
            display: none;
            top: 32px;
            left: 50%;
            z-index: 9999;
            pointer-events: none;
            transform: translateX(-50%);
            transition: opacity 0.2s ease,
            transform 0.2s ease;
            font-size: 13px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.46;

            & /deep/ .text {
              text-align: left;
              padding: 2px 7px;
            }
          }
        }
        .pv-sampling-tag {
          color: #fff;
          font-size: 12px;
          font-weight: 500;
          padding: 5px;
          border-radius: 4px;
          background: linear-gradient(93deg, #0074FF 0%, #1783BA 63.5%, #57AB00 100%);
          margin-right: 8px;
        }
        .pv-label {
          color: #767676;
          font-weight: 400;
        }
        .pv-limit {
          color: #2F2F2F;
          font-weight: 600;
        }
        .ipblock-tooltip {
          padding: 12px 16px;
          top: 40px;
          left: 50%;
          pointer-events: none;
          opacity: 0;
          transform: translate(-50%, -15px);
          transition: opacity 0.2s ease,
          transform 0.2s ease;

          &.large {
            width: 267px;
          }

          /deep/ .text {
            width: 100%;
          }

          // .tooltip-seperator {
          //   background-color: #D7D7D7;
          //   height: 1px;
          //   width: 100%;
          // }

          .tooltip-row {
            display: flex;
            justify-content: space-between;
            gap: 30px;
            padding: 10px 0;
            border-bottom: 1px solid #D7D7D7;
            text-align: left;
            width: 100%;
            box-sizing: border-box;
            &:last-child {
              border: 0;
            }
            .tip-title {
              margin-left: 0;
              color: #666666;
              margin-left: 0px;
              font-weight: 400;
              font-size: 13px;
              line-height: 16px;
              // display: flex;
              // align-items: center;

              .icon {
                vertical-align: top;
                margin-right: 5px;
              }

              span {
                margin-left: 0px;

              }
            }

            .tip-title-desc {
              display: inline-block;

              &.exhauted {
                color: #ec0047;
                font-weight: 500;
              }
            }

            .tip-content {
              color: #2f2f2f;
            }
          }
        }

        .tip {
          position: absolute;
          right: 0;
          top: -22px;
          margin-left: 0;
        }

        &.exhauted {
          span.pv-limit {
            color: #ec0047;
            font-weight: 500;
          }
        }

        &.has:hover {
          // margin-left: 0;
          background-color: rgba(0, 0, 0, 0.06);

          .ipblock-tooltip {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      }
    }

    &.is-ga {
      .basic-info {
        .main-info {
          opacity: 0.5;
          pointer-events: none;
        }
      }
    }
  }
</style>
